import { useMemo } from 'react';
import { addDays } from 'date-fns';

import {
  U21Modal,
  U21Form,
  U21FormField,
  U21FormFieldTypes,
  U21Spacer,
} from 'app/shared/u21-ui/components';

import {
  EntityLatestRiskScore,
  RiskModelSummary,
} from 'app/modules/riskRatings/responses';
import { useApplyRiskScoreOverride } from 'app/modules/riskRatings/queries/useApplyRiskScoreOverride';
import { RiskScorePicker } from 'app/modules/riskRatings/components/RiskScorePicker';
import { EntityRiskScoreDisplay } from 'app/modules/riskRatings/components/EntityRiskScore';

interface Props {
  open: boolean;
  onClose: () => void;
  latestRiskScore: EntityLatestRiskScore;
  riskModelSummary: RiskModelSummary;
}

interface OverrideFormValues {
  score: number;
  comment: string;
  expires_at?: Date;
}

const FORM_ID = 'risk-score-override-form';

export const RiskScoreOverrideModal = ({
  open,
  onClose,
  latestRiskScore,
  riskModelSummary,
}: Props) => {
  const { score } = latestRiskScore;
  const { high_risk_floor: highRiskFloor, low_risk_ceiling: lowRiskCeiling } =
    riskModelSummary;

  const { mutateAsync, isPending } = useApplyRiskScoreOverride(
    latestRiskScore.entity_external_id,
  );

  const initialValues: Partial<OverrideFormValues> = useMemo(
    () => ({ score }),
    [score],
  );

  return (
    <U21Modal
      open={open}
      onClose={onClose}
      subtitle={
        <U21Spacer horizontal useFlexGap>
          Current risk score:
          <EntityRiskScoreDisplay
            disablePopover
            latestRiskScore={latestRiskScore}
            riskModelSummary={riskModelSummary}
          />
        </U21Spacer>
      }
      title="Override Risk Score"
      actionButtonProps={{
        children: 'Apply Override',
        form: FORM_ID,
        type: 'submit',
        loading: isPending,
      }}
    >
      <U21Form<OverrideFormValues>
        id={FORM_ID}
        onSubmit={async (values: OverrideFormValues) => {
          try {
            await mutateAsync({
              score: values.score,
              comment: values.comment.trim(),
              expires_at: values.expires_at?.toISOString(),
            });
            onClose();
          } catch {}
        }}
        initialValues={initialValues}
      >
        <U21Spacer spacing={2}>
          <RiskScorePicker
            highRiskFloor={highRiskFloor}
            lowRiskCeiling={lowRiskCeiling}
          />
          <U21FormField
            label="Reason"
            name={'comment' satisfies keyof OverrideFormValues}
            type={U21FormFieldTypes.TEXTAREA}
            required
            fieldProps={{
              placeholder:
                'Describe the reason for this override (required for audit trail).',
            }}
            validate={(value?: string) =>
              !value?.trim() ? 'Reason is required.' : undefined
            }
          />
          <U21FormField
            label="Expiration"
            name={'expires_at' satisfies keyof OverrideFormValues}
            type={U21FormFieldTypes.DATE_TIME_PICKER}
            description="Override will automatically expire on this date."
            fieldProps={{
              minDate: addDays(new Date(), 1),
              placeholder: 'No expiration',
            }}
          />
        </U21Spacer>
      </U21Form>
    </U21Modal>
  );
};
